import { useMediaQuery } from '@finn/design-system/helpers/media';
import { useReadyValue } from '@finn/platform-modules';
import { Features, useIsABVariant } from '@finn/ua-featureflags';
import { getCloudinaryImgUrl, getCloudinarySrcSet } from '@finn/ui-utils';
import { useContext, useState } from 'react';

import { getLifestyleImage } from '../../../configuration';
import { VehicleContext } from '../../../product-cards';
import { ImageGalleryDesktopV2 } from './ImageGalleryDesktopV2';
import { ImageGalleryMobileV2 } from './ImageGalleryMobileV2';

type Props = {
  imageUrls: string[];
  showDisclaimer?: boolean;
  disclaimerText: string;
};

export const ImageGallery = (props: Props) => {
  const isMobileAfterLoad = useMediaQuery('sm');
  const isMobile = useReadyValue(isMobileAfterLoad, true);
  const { vehicle } = useContext(VehicleContext);
  const isExpLifestyleImages = useIsABVariant(Features.ExpPdpLifestyleImages);
  const lifestyleImage = getLifestyleImage(vehicle.brand?.id, vehicle.model);
  const hasLifestyleImage = isExpLifestyleImages && !!lifestyleImage;
  const [initialSlide, setInitialSlide] = useState(0);

  const imgOptions = {
    w: 720,
    dpr: 2,
    c: 'auto',
  };

  const imageUrls = props.imageUrls.map((imageUrl) =>
    getCloudinaryImgUrl(imageUrl, imgOptions)
  );

  if (hasLifestyleImage) {
    imageUrls.unshift(getCloudinaryImgUrl(lifestyleImage));
  }

  const imageThumbUrls = props.imageUrls.map((imageUrl) =>
    getCloudinaryImgUrl(imageUrl, {
      ...imgOptions,
      w: 72,
    })
  );

  const imageSrcSets = props.imageUrls.map((imageUrl) =>
    getCloudinarySrcSet(imageUrl ?? '', [360, 720], 2, imgOptions)
  );

  const vehicleName = `${vehicle.brand?.id} ${vehicle.model}`;

  const imageGalleryDesktopProps = {
    ...props,
    imageUrls,
    setInitialSlide,
    altText: vehicleName,
    hasLifestyleImage,
  };

  const imageGalleryMobileProps = {
    ...props,
    imageUrls,
    imageSrcSets,
    imageThumbUrls,
    initialSlide,
    isMobile,
    altText: vehicleName,
    hasLifestyleImage,
  };

  return (
    <>
      <div className={'block sm:hidden'}>
        <ImageGalleryMobileV2 {...imageGalleryMobileProps} />
      </div>
      <div className={'hidden sm:block'}>
        <ImageGalleryDesktopV2 {...imageGalleryDesktopProps} />
      </div>
    </>
  );
};
